import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {searchSite} from '../../api/search';

const SearchIcon = () => (
    <svg className="flex-none text-gray-400" width="1em" height="1em" viewBox="0 0 24 24" fill="none"
         xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M20 20L15.5 15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
);

const SearchBox = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const trimmed = query.trim();
        if (trimmed.length < 2) {
            setResults([]);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(() => {
            searchSite(trimmed, {signal: controller.signal})
                .then((data) => {
                    setResults(data.slice(0, 6));
                    setOpen(true);
                })
                .catch((err) => {
                    if (err.name !== 'AbortError') {
                        console.error(err);
                    }
                });
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query]);

    useEffect(() => {
        const handleDocumentClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleDocumentClick);
        return () => document.removeEventListener('mousedown', handleDocumentClick);
    }, []);

    const goToResults = () => {
        const trimmed = query.trim();
        if (!trimmed) return;
        setOpen(false);
        navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            goToResults();
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className="relative flex-none ml-2" ref={containerRef}>
            <div className="flex items-center border border-gray-200 rounded-full px-2 py-1 bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900">
                <SearchIcon/>
                <input
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    placeholder="Search"
                    aria-label="Search"
                    className="search-box-input ml-2 w-28 text-sm text-gray-700 placeholder-gray-400 bg-transparent"
                />
            </div>

            {open && results.length > 0 &&
                <div
                    className="absolute mt-2 w-56 bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900 rounded-xl overflow-hidden shadow-lg z-20 border border-gray-100 top-full -right-1 max-h-96 overflow-y-auto">
                    <ul className="min-w-full">
                        {results.map((result) => (
                            <li key={result.slug}>
                                <a href={result.path + result.slug}
                                   className="flex items-center px-4 py-2 dropdown-menu-text dark:text-blue-400 no-underline font-normal hover:text-red-700 dark:hover:text-gray-400 truncate"
                                   onClick={() => setOpen(false)}>
                                    {result.title}
                                </a>
                            </li>
                        ))}
                        <li>
                            <button type="button" onClick={goToResults}
                                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:text-red-700 dark:hover:text-gray-400">
                                See all results
                            </button>
                        </li>
                    </ul>
                </div>
            }
        </div>
    );
};

export default SearchBox;
